import { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Building2, Users, MapPin, Phone, ChevronRight, Plus, Bed, LayoutList, LayoutGrid } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { mockSites, mockSiteClients } from '@/data/mockData';
import { useAuth } from '@/contexts/AuthContext';
import { useClients } from '@/hooks/useClients';
import { useRooms } from '@/hooks/useRooms';

import SiteManagement from './SiteManagement';

interface Site {
  id: string;
  name: string;
  address?: string;
  phone?: string;
  capacity: number;
  is_active: boolean;
  current_occupancy?: number;
}

interface FacilitySelectorProps {
  onSiteSelect: (siteId: string, siteName: string) => void;
}

export default function FacilitySelector({ onSiteSelect }: FacilitySelectorProps) {
  const { isDemoMode, userRole, userSites } = useAuth();
  const { clients: dbClients = [] } = useClients();
  const { rooms: allRooms = [] } = useRooms();
  const totalRoomCapacity = useMemo(() => allRooms.reduce((sum: number, r: any) => sum + (r.capacity || 1), 0), [allRooms]);
  const beddedCount = useMemo(() => {
    return dbClients.filter((c: any) => c.room_id).length;
  }, [dbClients]);
  const [sites, setSites] = useState<Site[]>([]);
  const [loading, setLoading] = useState(false);
  const [showSiteManagement, setShowSiteManagement] = useState(false);
  const [viewMode, setViewMode] = useState<'list' | 'cards'>('list');

  // Calculate checked-in counts from actual client data
  const getCheckedInCount = (siteId: string) => {
    if (isDemoMode) {
      return (mockSiteClients[siteId as keyof typeof mockSiteClients] || [])
        .filter((c: any) => c.status === 'checked-in').length;
    }
    return dbClients.filter((c: any) => (c.site_id === siteId || c.siteId === siteId) && c.status === 'checked-in').length;
  };

  const getCheckedOutCount = (siteId: string) => {
    if (isDemoMode) {
      return (mockSiteClients[siteId as keyof typeof mockSiteClients] || [])
        .filter((c: any) => c.status === 'checked-out').length;
    }
    return dbClients.filter((c: any) => (c.site_id === siteId || c.siteId === siteId) && c.status === 'checked-out').length;
  };


  const getTotalCheckedIn = () => {
    return sites.reduce((sum, site) => sum + getCheckedInCount(site.id), 0);
  };

  useEffect(() => {
    loadSites();
  }, []);

  const loadSites = async () => {
    setLoading(true);
    try {
      if (isDemoMode) {
        setSites(mockSites);
      } else {
        const { data, error } = await supabase
          .from('sites')
          .select('*')
          .eq('is_active', true)
          .order('name');

        if (error) throw error;
        setSites(data || []);
      }
    } catch (error: any) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error.message
      });
    } finally {
      setLoading(false);
    }
  };

  const isSuperAdmin = userRole === 'super_admin';

  if (showSiteManagement) {
    return (
      <div className="space-y-4">
        <Button variant="outline" onClick={() => setShowSiteManagement(false)}>
          ← Back to Facilities
        </Button>
        <SiteManagement isDemo={isDemoMode} />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-semibold">Facility Management</h2>
          <p className="text-sm text-muted-foreground">Select a facility to view daily census</p>
        </div>
        <div className="flex items-center gap-2">
          {/* View Toggle */}
          <div className="flex items-center border rounded-lg overflow-hidden">
            <Button
              variant={viewMode === 'list' ? 'default' : 'ghost'}
              size="sm"
              className="rounded-none px-3"
              onClick={() => setViewMode('list')}
            >
              <LayoutList className="h-4 w-4" />
            </Button>
            <Button
              variant={viewMode === 'cards' ? 'default' : 'ghost'}
              size="sm"
              className="rounded-none px-3"
              onClick={() => setViewMode('cards')}
            >
              <LayoutGrid className="h-4 w-4" />
            </Button>
          </div>
          {isSuperAdmin && (
            <Button onClick={() => setShowSiteManagement(true)} size="sm">
              <Plus className="h-4 w-4 mr-2" />
              Manage Sites
            </Button>
          )}
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-green-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Current Occupancy</p>
                <p className="text-2xl font-bold text-green-600">{getTotalCheckedIn()}</p>
              </div>
              <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center">
                <Users className="h-5 w-5 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-primary/30">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Available Beds</p>
                <p className="text-2xl font-bold text-primary">
                  {Math.max(0, totalRoomCapacity - beddedCount)}
                </p>
              </div>
              <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                <Bed className="h-5 w-5 text-primary" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-amber-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Total Capacity</p>
                <p className="text-2xl font-bold text-amber-600">
                  {sites.reduce((sum, s) => sum + (s.capacity || 0), 0)}
                </p>
              </div>
              <div className="h-10 w-10 rounded-full bg-amber-100 flex items-center justify-center">
                <Building2 className="h-5 w-5 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Facilities List */}
      {sites.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-8">
            <Building2 className="h-10 w-10 text-muted-foreground mb-3" />
            <h3 className="text-base font-semibold mb-1">No Facilities Yet</h3>
            <p className="text-sm text-muted-foreground text-center mb-3">
              Set up your first facility to begin managing your shelter
            </p>
            {isSuperAdmin && (
              <Button onClick={() => setShowSiteManagement(true)} size="sm">
                <Plus className="h-4 w-4 mr-2" />
                Add Your First Facility
              </Button>
            )}
          </CardContent>
        </Card>
      ) : viewMode === 'list' ? (
        <Card>
          <CardContent className="p-0">
            <div className="divide-y">
              {sites.map(site => {
                const siteOccupancy = getCheckedInCount(site.id);
                const siteCapacity = site.capacity || 0;
                const occupancyRate = siteCapacity > 0 ? (siteOccupancy / siteCapacity) * 100 : 0;
                
                return (
                  <div 
                    key={site.id} 
                    onClick={() => onSiteSelect(site.id, site.name)}
                    className="flex items-center justify-between p-4 hover:bg-muted/50 cursor-pointer transition-colors"
                  >
                    <div className="flex items-center gap-3 flex-1">
                      <div className="h-8 w-8 rounded bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Building2 className="h-4 w-4 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-sm font-semibold">{site.name}</h3>
                        {site.address && (
                          <p className="text-xs text-muted-foreground truncate flex items-center gap-1">
                            <MapPin className="h-3 w-3" />
                            {site.address}
                          </p>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-6 flex-shrink-0">
                      {site.phone && (
                        <div className="text-right hidden md:block">
                          <p className="text-xs text-muted-foreground">Contact</p>
                          <p className="text-sm">{site.phone}</p>
                        </div>
                      )}
                      <div className="text-right">
                        <p className="text-xs text-muted-foreground">Occupancy</p>
                        <p className="text-sm font-semibold">{siteOccupancy} / {siteCapacity}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-muted-foreground">Rate</p>
                        <p className="text-sm font-semibold">{occupancyRate.toFixed(0)}%</p>
                      </div>
                      <Badge variant={site.is_active ? 'default' : 'secondary'}>
                        {site.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      ) : (
        /* Card/Button View */
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {sites.map(site => {
            const siteOccupancy = getCheckedInCount(site.id);
            const siteCapacity = site.capacity || 0;
            const occupancyRate = siteCapacity > 0 ? (siteOccupancy / siteCapacity) * 100 : 0;
            
            return (
              <Card
                key={site.id}
                onClick={() => onSiteSelect(site.id, site.name)}
                className="cursor-pointer hover:shadow-md hover:border-primary/40 transition-all duration-200 hover:-translate-y-0.5"
              >
                <CardContent className="p-5">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Building2 className="h-5 w-5 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold truncate">{site.name}</h3>
                      {site.address && (
                        <p className="text-xs text-muted-foreground truncate flex items-center gap-1">
                          <MapPin className="h-3 w-3 flex-shrink-0" />
                          {site.address}
                        </p>
                      )}
                    </div>
                    <Badge variant={site.is_active ? 'default' : 'secondary'} className="flex-shrink-0">
                      {site.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>

                  <div className="grid grid-cols-3 gap-3 mb-3">
                    <div className="text-center p-2 bg-green-50 border border-green-200 rounded-lg">
                      <p className="text-lg font-bold text-green-700">{siteOccupancy}</p>
                      <p className="text-[10px] text-muted-foreground">Occupied</p>
                    </div>
                    <div className="text-center p-2 bg-primary/5 border border-primary/20 rounded-lg">
                      <p className="text-lg font-bold text-primary">{siteCapacity - siteOccupancy}</p>
                      <p className="text-[10px] text-muted-foreground">Available</p>
                    </div>
                    <div className="text-center p-2 bg-amber-50 border border-amber-200 rounded-lg">
                      <p className="text-lg font-bold text-amber-700">{siteCapacity}</p>
                      <p className="text-[10px] text-muted-foreground">Capacity</p>
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs text-muted-foreground">Occupancy Rate</span>
                      <span className="text-xs font-semibold">{occupancyRate.toFixed(0)}%</span>
                    </div>
                    <div className="w-full bg-muted rounded-full h-2">
                      <div 
                        className="bg-primary rounded-full h-2 transition-all" 
                        style={{ width: `${occupancyRate}%` }}
                      />
                    </div>
                  </div>

                  {site.phone && (
                    <div className="mt-3 pt-3 border-t flex items-center gap-1 text-xs text-muted-foreground">
                      <Phone className="h-3 w-3" />
                      {site.phone}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}